<template>
  <div class="workpermit-content-slot">
    <GeneralInfo
      ref="generalInfo"
      :isEdit="false"
      :isProjectAvailable="isProjectAvailable"
      :isStandalone="isStandalone"
    />
    <a
      v-if="showSaTncLink"
      @click="() => openSaWpTncModal()"
    >
      {{ $t('view') }} {{ $t('tncName') }}
    </a>
    <a
      class="workpermit-content-slot__view-workerinfo"
      @click="() => viewWorkerInfo()"
    >
      <img
        alt="worker info"
        src="/ic_worker_info.png"
      >
      {{ $t('view') }} {{ $t('workPermit.workerInfo') }}
    </a>
    <template v-if="showContractorHistoryCommentsSection">
    <div
      v-for="item in historyBlocksForViewer"
      :key="item.status"
      class="workpermit-content-slot__review-box"
      :class="{
        'workpermit-content-slot__review-box--history-split': historyRowUsesEngineerReviewerSplit(item),
        'workpermit-content-slot__review-box--tenant-external-only': item.externalContent && !isEngineer,
      }"
    >
      <!-- Engineer: APPR — final single; body via approverHistoryBodyText or --. -->
      <template v-if="historyRowUsesEngineerApproverSingle(item)">
        <WpHistoryCommentHeading
          :index="item.index"
          :title="item.title"
          :byline="item.by"
        />
        <pre class="workpermit-content-slot__history-pre">{{
          historyCommentDisplay(approverHistoryBodyText(item)) || emptyValue
        }}</pre>
      </template>

      <!-- Engineer: non-APPR rows split when contractor-facing text is non-empty. -->
      <template v-else-if="historyRowUsesEngineerReviewerSplit(item)">
        <WpHistoryDisclosureGrid
          v-if="historyRowUsesDisclosureGrid(item)"
          :item="item"
          :internal-body="historyCommentDisplay(item.content)"
          :external-body="historyCommentDisplay(item.externalContent)"
          :disclosure-checked="historyRowDisclosureChecked(item)"
          :disclosure-disabled="!historyRowShowsExternalApproverChrome(item.status)"
          @disclosure-change="setHistoryExternalDisclosed(item.status, $event)"
        />
        <div
          v-else
          class="workpermit-content-slot__history-split-body"
        >
          <div class="workpermit-content-slot__history-split-heading-row">
            <WpHistoryCommentHeading
              :index="item.index"
              :title="item.title"
              :byline="item.by"
            />
          </div>
          <div class="workpermit-content-slot__history-split-left">
            <WpHistoryInternalCommentBlock :text="historyCommentDisplay(item.content)" />
          </div>
          <WpHistoryExternalCommentsColumn
            :external-display-text="historyCommentDisplay(item.externalContent)"
          />
        </div>
      </template>

      <!-- Tenant: external comment only when !engineer, not APPR/REJECT, and this row has external content. -->
      <template v-else-if="historyRowUsesTenantExternalOnly(item)">
        <WpHistoryCommentHeading
          :index="item.index"
          :title="item.title"
          :byline="item.by"
        />
        <pre class="workpermit-content-slot__history-pre">{{ historyCommentDisplay(item.externalContent) }}</pre>
      </template>

      <!-- Tenant: final APPR/REJECT row — single body via approverHistoryBodyText or --. -->
      <template v-else-if="historyRowUsesTenantApproverSingle(item)">
        <WpHistoryCommentHeading
          :index="item.index"
          :title="item.title"
          :byline="item.by"
        />
        <pre class="workpermit-content-slot__history-pre">{{
          historyCommentDisplay(approverHistoryBodyText(item)) || emptyValue
        }}</pre>
      </template>
      <template v-else-if="historyRowUsesEngineerInternalOrEmpty(item)">
        <WpHistoryCommentHeading
          :index="item.index"
          :title="item.title"
          :byline="item.by"
        />
        <template v-if="!historyTextMeaningful(item.content) && !historyTextMeaningful(item.externalContent)">
          <pre class="workpermit-content-slot__history-pre">{{ emptyValue }}</pre>
        </template>
        <WpHistoryInternalCommentBlock
          v-else
          :text="historyCommentDisplay(item.content)"
        />
      </template>
    </div>
    <template v-if="showTenantExternalCommentsSection">
      <div
        v-for="(item, idx) in tenantExternalCommentRows"
        :key="`tenant-external-dedup-${idx}`"
        :class="[
          'workpermit-content-slot__review-box',
          { 'workpermit-content-slot__review-box--tenant-external-only': !isEngineer },
        ]"
      >
        <WpHistoryCommentHeading
          :index="historyBlocksForViewer.length + idx + 1"
          :title="item.title"
          :byline="item.by"
        />
        <pre class="workpermit-content-slot__history-pre">{{
          historyCommentDisplay(item.text)
        }}</pre>
      </div>
    </template>
    </template>
    <div ref="approveProcess">
      <template v-if="isPP">
        <template v-if="(canApprovePPInStep) && isProjectAvailable">
          <div class="workpermit-content-slot__btns float-right">
            <HKLButton
              color="darkblue"
              action="rejectAction"
              @click="() => openRejectPPModal()"
            />
            <HKLButton
              color="orange"
              action="approveAction"
              @click="openApproveProjectPermitModal"
            />
          </div>
        </template>
        <template v-else-if="canSecondReviewPPInStep">
          <div class="workpermit-content-slot__review-box">
            <WpReviewerCommentFields
              :internal-section-title="$t('workPermit.reviewerInternalSection2nd')"
              :external-section-title="$t('workPermit.reviewerExternalSection2nd')"
              :internal-value="actionComment"
              :external-value="externalComment"
              :leave-external-value="leaveExternalComments"
              :external-error="rejectExternalFieldError"
              :readonly="!isProjectAvailable"
              @update:internalValue="onActionCommentUpdate"
              @update:externalValue="onExternalCommentUpdate"
            />
            <div class="workpermit-content-slot__btns">
              <HKLButton
                color="darkblue"
                action="rejectAction"
                @click="() => openRejectProcessModal(WORK_PERMIT_STATUS.REVIEWED)"
              />
              <HKLButton
                color="orange"
                action="nextReviewer"
                @click="() => openNextReviewerModal(WP_ACTION_TYPE.PPApprove)"
              />
            </div>
            <WorkPermNextReviewerModal
              :ref="`${WP_ACTION_TYPE.PPApprove}_nextReviewerModal_${permitData.id}`"
              @multi-select-submited="confirmPPApprover"
            />
          </div>
        </template>
        <template v-else-if="canReviewPP && isWAPPR">
          <div class="workpermit-content-slot__review-box">
            <WpReviewerCommentFields
              :internal-section-title="$t('workPermit.reviewerInternalSection1st')"
              :external-section-title="$t('workPermit.reviewerExternalSection1st')"
              :internal-value="actionComment"
              :external-value="externalComment"
              :leave-external-value="leaveExternalComments"
              :external-error="rejectExternalFieldError"
              :readonly="!isProjectAvailable"
              @update:internalValue="onActionCommentUpdate"
              @update:externalValue="onExternalCommentUpdate"
            />
            <div
              v-show="isProjectAvailable"
              class="workpermit-content-slot__btns"
            >
              <HKLButton
                color="darkblue"
                action="rejectAction"
                @click="() => openRejectProcessModal(WORK_PERMIT_STATUS.ACKNOWLEDGED)"
              />
              <HKLButton
                color="orange"
                action="acknowledge"
                @click="() => openNextReviewerModal(WP_ACTION_TYPE.PPReview2)"
              />
            </div>
          </div>
          <WorkPermNextReviewerModal
            :ref="`${WP_ACTION_TYPE.PPReview2}_nextReviewerModal_${permitData.id}`"
            @multi-select-submited="confirmPPSecondReviewer"
          />
        </template>
      </template>
      <template v-if="canActionInWP">
        <div class="workpermit-content-slot__review-box">
          <WpReviewerCommentFields
            :internal-section-title="actionHintRef"
            :external-section-title="actionExternalTitleRef"
            :internal-value="reviewerPrimaryCommentValue"
            :external-value="externalComment"
            :leave-external-value="leaveExternalComments"
            :allow-leave-external-comments="allowLeaveExternalInReviewerForm"
            :sole-field-is-external="!allowLeaveExternalInReviewerForm"
            :external-error="rejectExternalFieldError"
            :readonly="!isProjectAvailable"
            @update:internalValue="onReviewerPrimaryCommentUpdate"
            @update:externalValue="onExternalCommentUpdate"
          />
          <div
            v-show="isProjectAvailable"
            class="workpermit-content-slot__btns"
          >
            <HKLButton
              color="darkblue"
              @click="() => openRejectProcessModal(nextStatusRef)"
            >
              {{ $t('action.rejectAction') }}
            </HKLButton>
            <HKLButton
              v-if="actionButtonRef"
              color="orange"
              v-on="actionButtonRef.on"
            >
              {{ actionButtonRef.label }}
            </HKLButton>
          </div>
          <WorkPermNextReviewerModal
            v-if="nextReviewerSubmitRef"
            ref="nextReviewerModal"
            @multi-select-submited="nextReviewerSubmitRef"
          />
        </div>
      </template>
    </div>
    <Modal ref="approveWorkPermitModal">
      <div
        slot="content"
        class="modal-pure-text"
      >
        {{ $t('workPermit.areYouApprove') }}
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="() => $refs.approveWorkPermitModal.close()"
        >
          {{ $t('action.cancel') | capitalize }}
        </button>
        <button
          class="btn btn--orange"
          @click="() => approveWP()"
        >
          {{ $t('action.yes') | capitalize }}
        </button>
      </div>
    </Modal>
    <Modal ref="approveProjectPermitModal">
      <div
        slot="content"
        class="modal-pure-text"
      >
        {{ $t('workPermit.areYouApprove') }}
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="() => $refs.approveProjectPermitModal.close()"
        >
          {{ $t('action.cancel') | capitalize }}
        </button>
        <button
          class="btn btn--orange"
          @click="() => approvePP()"
        >
          {{ $t('action.yes') | capitalize }}
        </button>
      </div>
    </Modal>
    <Modal ref="rejectProcessModal">
      <div
        class="modal-pure-text"
        solt="content"
      >
        {{ $t('workPermit.areYouReject') }}
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="() => $refs.rejectProcessModal.close()"
        >
          {{ $t('action.cancel') | capitalize }}
        </button>
        <button
          class="btn btn--orange"
          @click="() => rejectOnProcess()"
        >
          {{ $t('action.yes') | capitalize }}
        </button>
      </div>
    </Modal>
    <!-- For Approver to reject PP -->
    <Modal
      ref="rejectPPModal"
      :afterCloseModal="onRejectPPModalAfterClose"
    >
      <div
        class="modal-pure-text"
        solt="modal-header"
      >
        <span class="required-flag">
          {{ $t('tender.tmview.rejectPlaceholder') }}
        </span>
      </div>
      <div slot="content">
        <WpReviewerCommentFields
          :internal-section-title="$t('workPermit.reviewerInternalSection2nd')"
          :external-section-title="$t('workPermit.reviewerExternalSection2nd')"
          :internal-value="externalComment"
          :external-value="externalComment"
          :leave-external-value="leaveExternalComments"
          :allow-leave-external-comments="false"
          :sole-field-is-external="true"
          :omit-sole-field-chrome="true"
          :external-error="rejectExternalFieldError"
          :readonly="false"
          @update:internalValue="onExternalCommentUpdate"
          @update:externalValue="val => externalComment = val"
        />
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="() => $refs.rejectPPModal.close()"
        >
          {{ $t('action.cancel') | capitalize }}
        </button>
        <button
          class="btn btn--orange"
          @click="() => rejectPP()"
        >
          {{ $t('action.yes') | capitalize }}
        </button>
      </div>
    </Modal>
    <Modal
      ref="viewWorkerInfo"
      modalType="large-modal"
      :showCloseBtn="!editingWorkerInfo"
    >
      <WorkerInfo
        slot="content"
        :showOperate="canUpdateWorkerInfo && isProjectAvailable && isPermitAvailable"
        @editing:workerInfo="val => editingWorkerInfo = val"
      />
    </Modal>
    <ActionConfirmModal
      ref="SWPConflictModal"
      :modalId="`swp-conflict-${+new Date()}`"
      :confirmTitle="$t('standaloneWorkPermit.conflictWithWP')"
    />
    <SAWorkPermitTnC
      ref="saWpTnCModal"
      :companyName="companyName"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { getCurrentInstance, ref, unref, computed } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Modal from '@/controls/Modal'
import TextField from '@/controls/TextField'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { sendEmail } from '@/api/postman/request'
import { getWPConflictStatus } from '@/api/workPermit/request'
import { tmsPermsTypes } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import { tenantProjectStatus } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useStoreGetters } from '@/store/index'
import HKLButton from '../../controls/Button/HKLButton'
import SAWorkPermitTnC from './SAWorkPermitTnC.vue'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS, WP_ACTION_TYPE, WP_ACTION_TYPE_BY_NEXT_STATUS_MAPPING } from './consts'
import { disableTenantContractorEdit, getParsedWorkPermitHistoryActor, getWorkPermitHistoryRowStatus } from './utils'
import { buildWorkPermitHistoryDetail, getWorkPermitCommentMode, WORK_PERMIT_COMMENT_MODE } from './commentFlow'
import {
  WORK_PERMIT_HISTORY_DISPLAY_LAYOUT,
  approverHistoryBodyText,
  buildWorkPermitHistoryDisplayRows,
  canExternalViewerSeeHistorySection,
  canShowEngineerHistoryDisclosure,
  shouldShowEngineerHistoryDisclosureIndicator,
  getLatestWorkPermitHistoryDisplayCycle,
  getWorkPermitHistoryDisplayDecision,
  historyTextMeaningful,
  isActiveReviewStatus,
  resolveCommentContent,
} from './history/index'
import WorkerInfo from './WorkerInfo'
import WorkPermNextReviewerModal from './WorkPermNextReviewerModal'
import GeneralInfo from './GeneralInfo'
import WpReviewerCommentFields from './WpReviewerCommentFields'
import WpHistoryCommentHeading from './WpHistoryCommentHeading'
import WpHistoryInternalCommentBlock from './WpHistoryInternalCommentBlock'
import WpHistoryDisclosureGrid from './WpHistoryDisclosureGrid'
import WpHistoryExternalCommentsColumn from './WpHistoryExternalCommentsColumn'
import useWpNextStatus from './hooks/useWpNextStatus'
import { useSaWpTnC } from './hooks/useTnC'
import workPermitCollapseContentMixin from './workPermitCollapseContentMixin'

const WP_REVIEWER_HISTORY_STATUSES = [
  WORK_PERMIT_STATUS.ACKNOWLEDGED,
  WORK_PERMIT_STATUS.REVIEWED,
  WORK_PERMIT_STATUS.REVIEWED2,
]

export default {
  name: 'WPCollapseContent',
  components: {
    SAWorkPermitTnC,
    GeneralInfo,
    WorkerInfo,
    Modal,
    TextField,
    WpReviewerCommentFields,
    WpHistoryCommentHeading,
    WpHistoryInternalCommentBlock,
    WpHistoryDisclosureGrid,
    WpHistoryExternalCommentsColumn,
    WorkPermNextReviewerModal,
    ActionConfirmModal,
    HKLButton,
  },
  mixins: [workPermitCollapseContentMixin],
  props: {
    permitData: Object,
    isProjectAvailable: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const { currentWorkPermit } = useStoreGetters('workPermit', ['currentWorkPermit'])
    const mapping = {
      [WORK_PERMIT_STATUS.ACKNOWLEDGED]: {
        actionHint: i18n.t('workPermit.reviewerInternalSection1st'),
        actionExternalTitle: i18n.t('workPermit.reviewerExternalSection1st'),
        actionButton: {
          label: i18n.t('workPermit.acknowledge'),
          on: {
            click: proxy.callNextReviewModal,
          },
        },
        nextReviewerSubmit: proxy.afterNextReviewSubmit,
      },
      [WORK_PERMIT_STATUS.REVIEWED]: {
        actionHint: i18n.t('workPermit.reviewerInternalSection2nd'),
        actionExternalTitle: i18n.t('workPermit.reviewerExternalSection2nd'),
        actionButton: {
          label: i18n.t('action.nextReviewer'),
          on: {
            click: proxy.callNextReviewModal,
          },
        },
        nextReviewerSubmit: proxy.afterNextReviewSubmit,
      },
      [WORK_PERMIT_STATUS.REVIEWED2]: {
        actionHint: i18n.t('workPermit.reviewerInternalSection2nd'),
        actionExternalTitle: i18n.t('workPermit.reviewerExternalSection2nd'),
        actionButton: {
          label: i18n.t('action.nextReviewer'),
          on: {
            click: proxy.callNextReviewModal,
          },
        },
        nextReviewerSubmit: proxy.afterNextReviewSubmit,
      },
      [WORK_PERMIT_STATUS.APPR]: {
        actionHint: i18n.t('workPermit.subConCommentsTitle'),
        actionExternalTitle: '',
        actionButton: {
          label: i18n.t('action.approveAction'),
          on: {
            click: proxy.clickApprove,
          },
        },
      },
    }
    const actionComment = ref('')
    const externalComment = ref('')
    const leaveExternalComments = computed(() => {
      return !lodash.isEmpty(lodash.trim(String(unref(externalComment) || '')))
    })
    const { allowedNextRef, nextStatusRef, needPermTypeRef } = useWpNextStatus(proxy.canProcessCurrentWP)
    const curOptionRef = computed(() => mapping[unref(nextStatusRef)])
    const canActionInWPRef = computed(() => [
      proxy.isWP,
      unref(allowedNextRef) && proxy.canProcessCurrentWP(unref(needPermTypeRef)),
    ].every(Boolean))
    const { modal: saWpTnCModal, open: openSaWpTncModal } = useRefModal()
    const companyName = computed(() => {
      return lodash.get(unref(currentWorkPermit), 'tenancy.company.name', '')
    })
    const { hasSaWpTnC } = useSaWpTnC()
    const showSaTncLink = computed(() => {
      const hasAcceptedByTxt = !!lodash.get(unref(saWpTnCModal), 'acceptedByTxt')
      // historical data does not accepted SA TnC
      return proxy.isStandalone && hasAcceptedByTxt && unref(hasSaWpTnC)
    })
    return {
      showSaTncLink,
      companyName,
      saWpTnCModal,
      openSaWpTncModal,
      actionComment,
      externalComment,
      leaveExternalComments,
      allowedNextRef,
      nextStatusRef,
      needPermTypeRef,
      curOptionRef,
      canActionInWP: canActionInWPRef,
      actionHintRef: computed(() => lodash.get(mapping, [nextStatusRef.value, 'actionHint'])),
      actionExternalTitleRef: computed(() => lodash.get(mapping, [nextStatusRef.value, 'actionExternalTitle'], '')),
      actionButtonRef: computed(() => lodash.get(mapping, [nextStatusRef.value, 'actionButton'])),
      nextReviewerSubmitRef: computed(() => lodash.get(mapping, [nextStatusRef.value, 'nextReviewerSubmit'])),
      async rejectWpStep () {
        await proxy.updateStatus(
          WORK_PERMIT_STATUS.REJECTED,
          proxy.buildHistoryDetail({ isReject: true }),
        )
        proxy.resetActionComments()
      },
    }
  },
  data () {
    return {
      WORK_PERMIT_STATUS,
      emptyValue: '--',
      editingWorkerInfo: false,
      rejectProcessStatus: null,
      WP_ACTION_TYPE,
      /** Approver-only: local UI state for history disclosure checkbox (not persisted). */
      historyExternalDisclosed: {},
      /** PP final approver reject modal: main comment is auto-disclosed to Tenant/Contractor (no “leave external” tick). */
      ppFinalApproverRejectFlow: false,
      /**
       * author: alvis
       * timestamp: 2026-04-22
       * description: When true, the external review field shows the reject-required error style until non-blank.
       * parameter: n/a (reactive data flag)
       */
      rejectExternalFieldError: false,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isEngineer', 'isContractor']),
    ...mapGetters('projectContractors', ['myContractor', 'isCurrentVendorAccount']),
    ...mapGetters('workPermit', ['currentWorkPermit', 'isStandalone', 'isTenantHasStandaloneAccess']),
    ...mapGetters('projects', ['currentProject']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    innerData () {
      return Object.assign({}, this.permitData)
    },
    isPP () {
      return this.innerData.type === WORK_PERMIT_TYPES.PROJECT
    },
    isWP () {
      return this.innerData.type === WORK_PERMIT_TYPES.WORK
    },
    canReviewPP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.reviewPP)
    },
    canSecondReviewPP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.secondReviewPP)
    },
    canApprovePP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approvePP)
    },
    canApprovePPInStep () {
      const PPStatus = this.innerData.status
      switch (PPStatus) {
        case WORK_PERMIT_STATUS.WAPPR:
          return this.canReviewPP && this.canSecondReviewPP & this.canApprovePP
        case WORK_PERMIT_STATUS.ACKNOWLEDGED:
          return this.canSecondReviewPP & this.canApprovePP
        case WORK_PERMIT_STATUS.REVIEWED:
          return this.canApprovePP
        default:
          return false
      }
    },
    canSecondReviewPPInStep () {
      const PPStatus = this.innerData.status
      switch (PPStatus) {
        case WORK_PERMIT_STATUS.WAPPR:
          return this.canReviewPP && this.canSecondReviewPP
        case WORK_PERMIT_STATUS.ACKNOWLEDGED:
          return this.canSecondReviewPP
        default:
          return false
      }
    },
    canReviewWP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.reviewWP)
    },
    canApproveWP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approveWP)
    },
    /** WP: hide “Leave comments for Contractor” on final approve/reject; that text is always disclosed to Tenant/Contractor via buildHistoryDetail. */
    allowLeaveExternalInReviewerForm () {
      if (!this.isWP) return true
      return this.nextStatusRef !== WORK_PERMIT_STATUS.APPR
    },
    isApproverRoleForHistory () {
      if (!this.isEngineer) return false
      if (this.isWP) return this.canApproveWP
      if (this.isPP) return this.canApprovePP
      return false
    },
    /** Disclosure chrome is editable only while the permit is in active review statuses. */
    showHistoryExternalDisclosureCheckbox () {
      return isActiveReviewStatus(this.innerData.status)
    },
    canContractorUpdateWorkerInfo () {
      const contractorId = deep(this.permitData, 'contractor.id')
      const isStandalone = this.isStandalone
      const canContractorEdit = this.isPP || !disableTenantContractorEdit()
      const standaloneWPUpdate = this.isContractor || this.isTenantHasStandaloneAccess
      const normalWPUpdate = (deep(this.myContractor, 'companyId') === contractorId && canContractorEdit) ||
        (this.isCurrentVendorAccount && contractorId === this.currentUser.externalId)
      return isStandalone ? standaloneWPUpdate : normalWPUpdate
    },
    canUpdateWorkerInfo () {
      return this.isProjectAvailable && !this.isCompleted && (
        this.isEngineer
          ? this.canProcessCurrentWP(tmsPermsTypes.workpermit.updateWorkers)
          : this.canContractorUpdateWorkerInfo
      )
    },
    sortedHistories () {
      return lodash.sortBy(lodash.cloneDeep(this.innerData.histories || []), h => {
        const t = h && (h.createdAt != null && h.createdAt !== '' ? h.createdAt : lodash.get(h, 'dataValues.createdAt'))
        return new Date(t)
      })
    },
    latestReviewCycleHistories () {
      return getLatestWorkPermitHistoryDisplayCycle(this.sortedHistories)
    },
    showContractorHistoryCommentsSection () {
      if (this.isEngineer) return true
      return canExternalViewerSeeHistorySection(this.innerData.status)
    },
    historyBlocksForViewer () {
      return buildWorkPermitHistoryDisplayRows({
        histories: this.innerData.histories || [],
        isEngineer: this.isEngineer,
        permitStatus: this.innerData.status,
        emptyValue: this.emptyValue,
        translate: (key, args) => this.$t(key, args),
        formatDate: (value, format) => this.$options.filters['formatDate'](value, format),
        historyWhoLabel: row => this.historyWhoLabel(row),
      })
    },
    tenantExternalCommentRows () {
      if (this.isEngineer || this.innerData.status !== WORK_PERMIT_STATUS.APPR) return []
      const blocks = this.historyBlocksForViewer || []
      return this.latestReviewCycleHistories
        .filter(h => {
          const d = h.detail || {}
          if (!d.leaveExternalComments || !d.externalComments) return false
          const raw0 = getWorkPermitHistoryRowStatus(h)
          const c0 = this.canonicalWorkPermitHistoryStatus(raw0)
          const st0 = c0 != null ? c0 : raw0
          if ([WORK_PERMIT_STATUS.APPR, WORK_PERMIT_STATUS.REJECTED].includes(st0)) return false
          return !!String(d.externalComments).trim()
        })
        .filter(h => {
          const raw1 = getWorkPermitHistoryRowStatus(h)
          const c1 = this.canonicalWorkPermitHistoryStatus(raw1)
          const st = c1 != null ? c1 : raw1
          const ext = String(lodash.get(h, 'detail.externalComments') || '').trim()
          const block = blocks.find(b => b.status === st)
          if (block && block.externalContent && String(block.externalContent).trim() === ext) {
            return false
          }
          return true
        })
        .map(h => {
          const hCreated = h.createdAt != null ? h.createdAt : lodash.get(h, 'dataValues.createdAt')
          const raw2 = getWorkPermitHistoryRowStatus(h)
          const c2 = this.canonicalWorkPermitHistoryStatus(raw2)
          const st = c2 != null ? c2 : raw2
          const t = this.resolveTitle(st)
          const title = t != null ? t : this.$t('workPermit.commentsfromReviewer')
          return {
            title,
            text: h.detail.externalComments,
            by: this.$t('workPermit.historyCommentByOn', [
              this.historyWhoLabel(h),
              this.$options.filters['formatDate'](hCreated, 'DD MMM YY HH:mm'),
            ]),
          }
        })
    },
    showTenantExternalCommentsSection () {
      return !this.isEngineer && this.tenantExternalCommentRows.length > 0
    },
    projectId () {
      return this.$router.currentRoute.params.id
    },
    reviewerCommentMode () {
      return getWorkPermitCommentMode({
        isWP: this.isWP,
        allowLeaveExternalInReviewerForm: this.allowLeaveExternalInReviewerForm,
        ppFinalApproverRejectFlow: this.ppFinalApproverRejectFlow,
      })
    },
    isSoleExternalCommentMode () {
      return this.reviewerCommentMode === WORK_PERMIT_COMMENT_MODE.SOLE_EXTERNAL
    },
    reviewerPrimaryCommentValue () {
      return this.isSoleExternalCommentMode ? this.externalComment : this.actionComment
    },
  },
  methods: {
    ...mapActions('workPermit', ['changeWorkPermitStatus', 'getWorkPermitById', 'getWorkPermitsInProjectByProjectId']),
    ...mapActions('tenantProject', ['loadTenantProject', 'updateTenantProjectStatus']),
    getHistoryRowCreatedAt (row) {
      if (!row) return null
      if (row.createdAt != null && row.createdAt !== '') {
        return row.createdAt
      }
      return lodash.get(row, 'dataValues.createdAt')
    },
    /**
     * Pick the earliest row in one status bucket as the representative item for the cycle.
     */
    pickRepresentativeHistory (rows) {
      if (!Array.isArray(rows) || rows.length === 0) return null
      const sorted = lodash.sortBy(rows, item => {
        const t = this.getHistoryRowCreatedAt(item)
        return t ? new Date(t).getTime() : 0
      })
      return sorted[0] || null
    },
    resolveCommentContent (status, detail) {
      return resolveCommentContent(status, detail, this.emptyValue)
    },
    /**
     * Section label for “Comments from …” (aligned with 1.17.3: by history `status` only).
     * `REJECTED` and review steps → Reviewer; `APPR` → Approver.
     * @param {string} status - history row `status`
     */
    resolveTitle (status) {
      if ([
        WORK_PERMIT_STATUS.ACKNOWLEDGED,
        WORK_PERMIT_STATUS.REVIEWED,
        WORK_PERMIT_STATUS.REVIEWED2,
        WORK_PERMIT_STATUS.REJECTED,
      ].includes(status)) {
        return this.$t('workPermit.commentsfromReviewer')
      }
      if (status === WORK_PERMIT_STATUS.APPR) {
        return this.$t('workPermit.commentsfromApprover')
      }
      return undefined
    },
    /**
     * Aligns API/DB `history.status` with `WORK_PERMIT_STATUS` (e.g. same casing) so
     * latest-cycle external row logic can compare stable status keys.
     * @param {string|*} raw - History row `status` value
     * @returns {string|*} Canonical status or original `raw`
     */
    canonicalWorkPermitHistoryStatus (raw) {
      if (raw == null) return null
      const values = Object.values(WORK_PERMIT_STATUS)
      for (var i = 0; i < values.length; i += 1) {
        if (raw === values[i]) {
          return values[i]
        }
      }
      if (typeof raw === 'string') {
        var lower = raw.trim().toLowerCase()
        for (var j = 0; j < values.length; j += 1) {
          if (values[j] && String(values[j]).toLowerCase() === lower) {
            return values[j]
          }
        }
      }
      return raw
    },
    historyCommentDisplay (text) {
      if (text == null || text === '') return text
      return String(text).replace(/\s+$/u, '')
    },
    /** @param {Object|*} rowOrRaw - Full history row from `permit.histories` (preferred) or a raw `who` value. */
    historyWhoLabel (rowOrRaw) {
      const w = getParsedWorkPermitHistoryActor(rowOrRaw)
      if (!w) return this.emptyValue
      const fromDisplay = w.displayName != null && String(w.displayName).trim()
      if (fromDisplay) return fromDisplay
      const fromUser = w.username != null && String(w.username).trim()
      if (fromUser) return fromUser
      return this.emptyValue
    },
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: Returns true if a history line has non-blank, non-placeholder display text.
     * @param {*} v - Raw value from history content or external line.
     * @returns {boolean}
     */
    historyTextMeaningful (v) {
      return historyTextMeaningful(v, this.emptyValue)
    },
    historyDisplayDecision (item) {
      if (item && item.decision) {
        return item.decision
      }
      return getWorkPermitHistoryDisplayDecision({
        item,
        isEngineer: this.isEngineer,
        emptyValue: this.emptyValue,
      })
    },
    historyDisplayLayoutIs (item, layout) {
      return this.historyDisplayDecision(item).layout === layout
    },
    historyRowUsesEngineerApproverSingle (item) {
      return this.historyDisplayLayoutIs(item, WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.ENGINEER_APPROVER_SINGLE)
    },
    historyRowUsesEngineerReviewerSplit (item) {
      return this.historyDisplayLayoutIs(item, WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.ENGINEER_REVIEWER_SPLIT)
    },
    historyRowUsesTenantExternalOnly (item) {
      return this.historyDisplayLayoutIs(item, WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.TENANT_EXTERNAL_ONLY)
    },
    historyRowUsesTenantApproverSingle (item) {
      return this.historyDisplayLayoutIs(item, WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.TENANT_APPROVER_SINGLE)
    },
    historyRowUsesEngineerInternalOrEmpty (item) {
      return this.historyDisplayLayoutIs(item, WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.ENGINEER_INTERNAL_OR_EMPTY)
    },
    /** Tenant: approver block is external-only; prefer externalContent, else legacy internal field. */
    approverHistoryBodyText (item) {
      return approverHistoryBodyText(item, this.emptyValue)
    },
    historyRowShowsExternalApproverChrome (rowStatus) {
      return canShowEngineerHistoryDisclosure({
        isEngineer: this.isEngineer,
        approverSecurity: this.isApproverRoleForHistory,
        currentStatus: lodash.get(this.innerData, 'status'),
        rowStatus,
      })
    },
    /** Disclosure grid with checkbox when editable (approver + active review) or read-only indicator from history. */
    historyRowUsesDisclosureGrid (item) {
      const rowStatus = item.status
      return (
        this.historyRowShowsExternalApproverChrome(rowStatus) ||
        shouldShowEngineerHistoryDisclosureIndicator({
          currentPermitStatus: lodash.get(this.innerData, 'status'),
          rowStatus,
        })
      )
    },
    historyRowDisclosureChecked (item) {
      return this.historyExternalDisclosedChecked(item.status)
    },
    historyExternalDisclosedChecked (rowStatus) {
      if (lodash.has(this.historyExternalDisclosed, rowStatus)) {
        return !!this.historyExternalDisclosed[rowStatus]
      }
      // Editable checkbox during active review: default checked. Intermediate history rows persist
      // showExternalCommentToContractor:false as a submit placeholder only; not the UI display source.
      if (this.historyRowShowsExternalApproverChrome(rowStatus)) {
        return true
      }
      return this.persistedExternalDisclosedForReviewerStatus(rowStatus)
    },
    /**
     * Read-only disclosure indicator state from persisted history (`showExternalCommentToContractor` when present).
     * @param {string} rowStatus
     * @returns {boolean}
     */
    persistedExternalDisclosedForReviewerStatus (rowStatus) {
      const row = (this.historyBlocksForViewer || []).find(b => b.status === rowStatus)
      if (!row || typeof row.persistedExternalDisclosedToContractor !== 'boolean') {
        return true
      }
      return row.persistedExternalDisclosedToContractor
    },
    setHistoryExternalDisclosed (rowStatus, value) {
      this.$set(this.historyExternalDisclosed, rowStatus, !!value)
    },
    buildReviewerExternalDisclosureByStatus () {
      return WP_REVIEWER_HISTORY_STATUSES.reduce((result, status) => {
        result[status] = this.historyExternalDisclosedChecked(status)
        return result
      }, {})
    },
    viewWorkerInfo () {
      this.$refs.viewWorkerInfo.open()
    },
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: Maps current review form (internal, external, leave tick) to history `detail` for the API. Single-external path stores text only in `externalComments`; two-column path stores `comments` plus optional `externalComments`. `showExternalCommentToContractor` is set for reject and final approve so external viewer APIs can expose the intended text.
     * @param {{ isReject?: boolean, toStatus?: string }} [options] - `isReject` for reject; `toStatus` for the target status.
     * @returns {Object} Object suitable for work permit history / status change payload.
     */
    buildHistoryDetail (options = {}) {
      let toStatus = options && options.toStatus
      if (!toStatus && options && options.isReject === true) {
        toStatus = WORK_PERMIT_STATUS.REJECTED
      }
      const reviewerExternalDisclosureByStatus = this.buildReviewerExternalDisclosureByStatus()
      const detail = buildWorkPermitHistoryDetail({
        mode: this.reviewerCommentMode,
        toStatus,
        internalComment: this.actionComment,
        externalComment: this.externalComment,
        reviewerExternalDisclosureByStatus,
      })
      if (toStatus === WORK_PERMIT_STATUS.APPR) {
        detail.showExternalCommentToContractor = this.historyExternalDisclosedChecked(WORK_PERMIT_STATUS.APPR)
      }
      return detail
    },
    resetActionComments () {
      this.actionComment = ''
      this.externalComment = ''
      this.rejectExternalFieldError = false
      this.ppFinalApproverRejectFlow = false
    },
    onRejectPPModalAfterClose () {
      this.ppFinalApproverRejectFlow = false
    },
    normalizeHistoryDetailPayload (comments) {
      if (comments !== null && typeof comments === 'object' && !Array.isArray(comments)) {
        return comments
      }
      return { comments: comments || '' }
    },
    resolveStatusChangeEmailType (toBeStatus, permitType) {
      const isWP = permitType === WORK_PERMIT_TYPES.WORK
      const isPP = permitType === WORK_PERMIT_TYPES.PROJECT
      switch (toBeStatus) {
        case WORK_PERMIT_STATUS.REJECTED:
          return EmailTypes.workPermits.rejected
        case WORK_PERMIT_STATUS.ACKNOWLEDGED:
          return isPP ? EmailTypes.workPermits.nextReviewerPP : EmailTypes.workPermits.nextReviewerWP
        case WORK_PERMIT_STATUS.REVIEWED:
          return isWP ? EmailTypes.workPermits.nextSecondReviewerWP : EmailTypes.workPermits.nextApprover
        case WORK_PERMIT_STATUS.REVIEWED2:
          return isWP ? EmailTypes.workPermits.nextApproverWP : null
        default:
          return null
      }
    },
    patchEmailPermitRecipient (permit, nextReviewer) {
      const email = lodash.get(nextReviewer, 'email')
      if (!email || !permit || !permit.manager) return permit
      permit.manager.username = email
      return permit
    },
    async updateStatus (toBeStatus, comments, nextReviewer) {
      let result
      if (!this.innerData.id) {
        formUtils.resetDirty()
        return result
      }

      const nextReviewerId = lodash.get(nextReviewer, 'id')
      const historyDetail = this.normalizeHistoryDetailPayload(comments)
      result = await this.changeWorkPermitStatus({status: toBeStatus, historyDetail, nextReviewerId})
      if (result && result.id) {
        this.innerData = result
        await this.getWorkPermitById(this.innerData.id)
        await this.getWorkPermitsInProjectByProjectId({ projectId: this.projectId })

        const emailType = this.resolveStatusChangeEmailType(toBeStatus, result.type)
        if (emailType) {
          const emailPermit = this.patchEmailPermitRecipient(lodash.clone(result), nextReviewer)
          await sendEmail(emailType, emailPermit)
        }
      }

      formUtils.resetDirty()
      return result
    },
    validation () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, this.$refs.approveProcess)
      const ok = fusionInputCheckUtil.isAllFieldsValid(this.$refs.approveProcess)
      if (!ok) {
        notification.warn(i18n.t('workPermit.reviewerCommentsMandatoryHint'))
      }
      return ok
    },
    validationRejectPP () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, this.$refs.rejectPPModal.$el)
      const ok = fusionInputCheckUtil.isAllFieldsValid(this.$refs.rejectPPModal.$el)
      if (!ok) {
        notification.warn(i18n.t('workPermit.reviewerCommentsMandatoryHint'))
      }
      return ok
    },
    /**
     * 拒絕必須填「對外」內容：雙欄看 `externalComment`；單欄（WP 最末關 / PP 最終拒絕彈層）也統一使用 `externalComment`。
     * @param {{ isRejectPpModal?: boolean }} [options] - `isRejectPpModal` 為真時使用彈層樣式（只紅框，不顯示 toast）。
     * @returns {boolean} True if reject may proceed, false if empty.
     */
    assertNonEmptyForRejectExternal (options) {
      const isPpModal = options && options.isRejectPpModal
      this.rejectExternalFieldError = false

      if (isPpModal) {
        if (lodash.isEmpty(lodash.trim(String(this.externalComment || '')))) {
          this.rejectExternalFieldError = true
          return false
        }
        return true
      }

      if (!this.allowLeaveExternalInReviewerForm) {
        if (lodash.isEmpty(lodash.trim(String(this.externalComment || '')))) {
          this.rejectExternalFieldError = true
          notification.error(i18n.t('workPermit.externalCommentsRequiredOnReject'))
          return false
        }
        return true
      }

      if (lodash.isEmpty(lodash.trim(String(this.externalComment || '')))) {
        this.rejectExternalFieldError = true
        notification.error(i18n.t('workPermit.externalCommentsRequiredOnReject'))
        return false
      }
      return true
    },
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: Syncs `externalComment` from `WpReviewerCommentFields` and clears `rejectExternalFieldError` when the user types a non-blank value.
     * @param {string} val - New external comment text.
     */
    onExternalCommentUpdate (val) {
      this.externalComment = val
      if (this.rejectExternalFieldError && !lodash.isEmpty(lodash.trim(String(val || '')))) {
        this.rejectExternalFieldError = false
      }
    },
    onReviewerPrimaryCommentUpdate (val) {
      if (this.isSoleExternalCommentMode) {
        this.onExternalCommentUpdate(val)
        return
      }
      this.onActionCommentUpdate(val)
    },
    /**
     * 拒絕紅框在左欄（sole-field、actionComment）時，填字後清除；雙欄僅外欄為空時不應因改內欄而清錯誤。
     */
    onActionCommentUpdate (val) {
      this.actionComment = val
    },
    openRejectProcessModal (status) {
      if (!this.validation()) return
      if (!this.assertNonEmptyForRejectExternal()) return
      this.$refs.rejectProcessModal.open()
      this.rejectProcessStatus = status
    },
    async rejectOnProcess () {
      if (unref(this.allowedNextRef)) {
        await this.rejectWpStep()
      } else {
        switch (this.rejectProcessStatus) {
          case WORK_PERMIT_STATUS.ACKNOWLEDGED:
            await this.firstRejectPP()
            break
          case WORK_PERMIT_STATUS.REVIEWED:
            await this.secondRejectPP()
            break
        }
      }
      this.$refs.rejectProcessModal.close()
    },
    async firstRejectPP () {
      await this.updateStatus(
        WORK_PERMIT_STATUS.REJECTED,
        this.buildHistoryDetail({ isReject: true }),
      )
      this.resetActionComments()
    },
    async secondRejectPP () {
      await this.updateStatus(
        WORK_PERMIT_STATUS.REJECTED,
        this.buildHistoryDetail({ isReject: true }),
      )
      this.resetActionComments()
    },
    async openNextReviewerModal (action) {
      if (!this.validation()) return
      await this.$refs[`${action}_nextReviewerModal_${this.permitData.id}`].openModal(action, this.innerData)
    },
    async confirmPPSecondReviewer (nextReviewer) {
      if (!this.validation()) return
      await this.updateStatus(
        WORK_PERMIT_STATUS.ACKNOWLEDGED,
        this.buildHistoryDetail({ toStatus: WORK_PERMIT_STATUS.ACKNOWLEDGED }),
        nextReviewer,
      )
      this.resetActionComments()
    },
    async confirmPPApprover (nextReviewer) {
      if (!this.validation()) return
      await this.updateStatus(
        WORK_PERMIT_STATUS.REVIEWED,
        this.buildHistoryDetail({ toStatus: WORK_PERMIT_STATUS.REVIEWED }),
        nextReviewer,
      )
      this.resetActionComments()
    },
    async confirmWPApprover (nextReviewer) {
      if (!this.validation()) return
      await this.updateStatus(
        WORK_PERMIT_STATUS.ACKNOWLEDGED,
        this.buildHistoryDetail({ toStatus: WORK_PERMIT_STATUS.ACKNOWLEDGED }),
        nextReviewer,
      )
      this.resetActionComments()
    },
    openApproveWorkPermitModal () {
      this.$refs.approveWorkPermitModal.open()
    },
    openApproveProjectPermitModal () {
      this.$refs.generalInfo.checkInsuranceDate({continueCallback: this.$refs.approveProjectPermitModal.open,
        date: new Date(this.currentWorkPermit.endDate),
        hint: i18n.t('workPermit.approveInsuranceNotCoverHint')})
    },
    async approveWP () {
      if (!this.validation()) return
      await this.updateStatus(
        WORK_PERMIT_STATUS.APPR,
        this.buildHistoryDetail({ toStatus: WORK_PERMIT_STATUS.APPR }),
      )
      this.$refs.approveWorkPermitModal.close()
      this.resetActionComments()
    },
    async approvePP () {
      const result = await this.updateStatus(
        WORK_PERMIT_STATUS.APPR,
        this.buildHistoryDetail({ toStatus: WORK_PERMIT_STATUS.APPR }),
      )
      if (result && result.status === WORK_PERMIT_STATUS.APPR && result.isTenantProject) {
        await this.updateTenantProjectStatus({
          request: { status: tenantProjectStatus.IN_PROGRESS },
        })
        await this.loadTenantProject(this.currentTenantProject.id)
      }
      this.$refs.approveProjectPermitModal.close()
    },
    openRejectPPModal () {
      this.resetActionComments()
      this.ppFinalApproverRejectFlow = true
      this.$refs.rejectPPModal.open()
    },
    async rejectPP () {
      if (!this.assertNonEmptyForRejectExternal({ isRejectPpModal: true })) return
      if (!this.validationRejectPP()) return
      await this.updateStatus(WORK_PERMIT_STATUS.REJECTED, this.buildHistoryDetail({ isReject: true }))
      this.resetActionComments()
      this.$refs.rejectPPModal.close()
    },
    async openNonTMNextReviewerModal (action) {
      await this.$refs.nextReviewerModal.openModal(action, this.innerData)
    },
    async callNextReviewModal () {
      if (!this.validation()) return
      const action = WP_ACTION_TYPE_BY_NEXT_STATUS_MAPPING[unref(this.nextStatusRef)]
      const hasConflict = await getWPConflictStatus(this.currentWorkPermit.id)
      if (!hasConflict) {
        this.openNonTMNextReviewerModal(action)
        return
      }
      this.$refs.SWPConflictModal.open(async () => {
        if (!this.validation()) return
        await this.openNonTMNextReviewerModal(action)
      })
    },
    async afterNextReviewSubmit (nextReviewer) {
      if (!this.validation()) return
      const nextStatus = this.nextStatusRef
      await this.updateStatus(
        nextStatus,
        this.buildHistoryDetail({ toStatus: nextStatus }),
        nextReviewer,
      )
      this.resetActionComments()
    },
    async clickApprove () {
      if (!this.validation()) return
      const hasConflict = await getWPConflictStatus(this.currentWorkPermit.id)
      if (!hasConflict) {
        this.openApproveWorkPermitModal()
        return
      }
      this.$refs.SWPConflictModal.open(() => {
        this.approveWP()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/workpermit/style';
@import '~styles/popup';
.workpermit-content-slot {
  &__view-workerinfo {
    margin: 25px 0 20px
  }
  .workpermit-content-slot__review_hint {
    font-weight: bold;
    font-size: 14px;
  }
  .workpermit-content-slot__external-label {
    margin-top: 12px;
    margin-bottom: 6px;
  }
  .workpermit-content-slot__history-comments {
    margin-top: 6px;
  }

  /* Left: heading + internal | right: external (checkbox + label + body) */
  .workpermit-content-slot__history-split-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 24px;
  }

  /* Full-width title row when parent renders heading above split; right column is external-comments-only. */
  .workpermit-content-slot__history-split-heading-row {
    grid-column: 1 / -1;
  }

  .workpermit-content-slot__history-split-left {
    min-width: 0;
    box-sizing: border-box;
  }

  /* Child blocks (internal/external label+pre): no parent scope on inner nodes */
  @media (max-width: 640px) {
    .workpermit-content-slot__history-split-body {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  ::v-deep .workpermit-content-slot__history-label {
    display: block;
    font-size: 12px;
    color: #878787;
    margin-bottom: 6px;
  }

  ::v-deep .workpermit-content-slot__history-pre {
    margin: 0;
    padding: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.43;
    min-height: 0;
    flex: 0 1 auto;
  }
}

</style>
